import { useObserver } from 'mobx-react-lite';
import { GqlDispensaries } from 'types/graphql';
import CartItem from 'src/state/cart-item';

import useUser from 'hooks/use-user';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import type {
  Cart as RemoteCart,
  CartItem as RemoteCartItem,
  User as RemoteUser,
  CommerceComponentsDataInterface,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import CartStore from 'src/state/cart';
import UserStore from 'src/state/user';

import { useBridgeActions } from './bridge-actions';
import { useBridgeDataLoaders } from './bridge-data-loaders';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

export const useBridgeDataInterface = (): CommerceComponentsDataInterface => {
  const { dispensary } = useDispensary() as { dispensary?: GqlDispensaries | undefined };
  const User = useUser();
  const UI = useUI();
  const Cart = useCart();
  const actions = useBridgeActions();
  const dataLoaders = useBridgeDataLoaders();

  const cart = useObserver(() => (Cart ? mapCartToRemote(Cart) : undefined));
  const user = useObserver(() => (User ? mapUserToRemote(User) : undefined));

  return {
    actions,
    cart,
    dataLoaders,
    location: mapDispensaryToRemote(dispensary),
    menuContext: UI.isKiosk ? 'kiosk' : 'store-front',
    user,
  };
};

const mapCartItem = (item: CartItem): RemoteCartItem => ({
  name: item.product?.name,
  price: item.price,
  productId: item.product?.id,
  quantity: item.quantity,
});

const mapCartToRemote = (cart: CartStore): RemoteCart => ({
  discount: cart.costBreakdown.credit,
  items: Object.values(cart.items).map(mapCartItem),
  subtotal: cart.costBreakdown.subtotal,
  tax: cart.costBreakdown.tax,
  total: cart.costBreakdown.total,
});

const mapUserToRemote = (user: UserStore): RemoteUser => ({
  birthday: user.birthday,
  email: (user.email as string | null) ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
});
